import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import Select from "react-select";
import { IReducer } from "../../state/reducer";
import { Check, X } from "react-feather";
import SetDetails from "../SetDetails/SetDetails";
import "./setRow.css";
import { BrickInfoTotal } from "../../state/BrickInfoTotal/BrickInfoTotal";
import { meetsCostRequirementsSets } from "../../state/BrickActions/Actions/_CostsAndOutcomes/meetsCostRequirementsSets";
import { ActionChange } from "../../state/BrickActions/ActionInfo/ActionInfoTypes";

const options = [
  { value: "1", label: "x1" },
  { value: "5", label: "x5" },
  { value: "10", label: "x10" },
  { value: "20", label: "x20" },
  { value: "25", label: "x25" },
  { value: "50", label: "x50" },
  { value: "75", label: "x75" },
  { value: "100", label: "x100" },
  { value: "150", label: "x150" },
  { value: "200", label: "x200" },
];

interface SetRowProps extends IReducer {
  setId: string;
}

const SetRow: React.FC<SetRowProps> = ({ setId, state, dispatch }) => {
  const setEntry = state.sets.get(setId);
  const action = state.actions.get(`buy_set_${setId}`);

  // If set doesn't exist or action doesn't exist, don't render
  if (!setEntry || !action) return null;

  const [showDetails, setShowDetails] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  // Get current buy amount from state (default to 1 if not set)
  const currentBuyAmount = state.setBuyAmounts.get(setId) ?? 1;
  const selectedOption =
    options.find((opt) => Number(opt.value) === currentBuyAmount) ?? options[0];
  const [popupPosition, setPopupPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
  }>({
    left: 0,
  });
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showPopup && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();

      // Estimate popup height (base height + costs)
      const estimatedPopupHeight = action.cost.length
        ? 60 + action.cost.length * 30 // Base + costs
        : 100; // Default fallback

      const spaceBelow = window.innerHeight - rect.bottom;
      const shouldPositionAbove = spaceBelow < estimatedPopupHeight + 20; // 20px buffer

      if (shouldPositionAbove) {
        // Position above: use bottom instead of top (no height calculation needed)
        setPopupPosition({
          bottom: window.innerHeight - rect.top + 5,
          left: rect.left,
        });
      } else {
        // Position below the button with 5px margin
        setPopupPosition({
          top: rect.bottom + 5,
          left: rect.left,
        });
      }
    }
  }, [showPopup, action.cost.length]);

  // Check if buy button should be enabled (costs met only, no capacity limit for sets)
  const canPurchase = () => {
    const buyAmount = Number(selectedOption.value) ?? 1;
    return meetsCostRequirementsSets(
      action,
      state.bricks,
      state.sets,
      buyAmount,
    );
  };

  const renderRequirementsMet = () => {
    const iconSize = 36;
    const buyAmount = Number(selectedOption.value) ?? 1;

    // Check if user can afford the currently selected amount (both bricks and sets)
    return meetsCostRequirementsSets(
      action,
      state.bricks,
      state.sets,
      buyAmount,
    ) ? (
      <Check size={iconSize} className={"headerIconBuyRow metCostIcon"} />
    ) : (
      <X size={iconSize} className={"headerIconBuyRow missingCostIcon"} />
    );
  };

  const renderCosts = (index: number, cost: ActionChange) => {
    const buyAmount = Number(selectedOption.value) ?? 1;
    const trueCost = cost.baseChangeValue * buyAmount;
    const costType = cost.type || "brick"; // Default to brick

    let currentAmount = 0;
    let name = "";
    let meetsRequirement = false;

    if (costType === "brick") {
      const brick = state.bricks.get(cost.brickId);
      currentAmount = brick?.amount ?? 0;
      name = BrickInfoTotal.get(cost.brickId)?.name ?? cost.brickId;
      meetsRequirement =
        brick && !isNaN(brick.amount) && brick.amount >= trueCost;
    } else if (costType === "set") {
      const set = state.sets.get(cost.brickId); // brickId field contains set ID
      currentAmount = set?.count ?? 0;
      name = set?.name ?? cost.brickId;
      meetsRequirement = set && set.count >= trueCost;
    }

    const displayAmount = isNaN(currentAmount) ? 0 : currentAmount;

    if (meetsRequirement) {
      return (
        <div className="popupCosts" key={index}>
          <Check size={20} className="costIcon metCostIcon" />
          {name} ({displayAmount}/{trueCost})
        </div>
      );
    } else {
      return (
        <div className="popupCosts" key={index}>
          <X size={20} className="costIcon missingCostIcon" />
          {name} ({displayAmount}/{trueCost})
        </div>
      );
    }
  };

  const executeQuickBuy = () => {
    dispatch({
      type: action.id as any, // Cast to avoid type issues
      buyAmount: Number(selectedOption.value) ?? 1,
    });
  };

  return (
    <>
      <div
        ref={buttonRef}
        className={`noSelect setRowContainer`}
        onMouseEnter={() => setShowPopup(true)}
        onMouseLeave={() => setShowPopup(false)}
      >
        <div className="setRowHeader">
          <div className="setRowTitle" onClick={() => setShowDetails(true)}>
            <div className="setTitleText">{setEntry.name}</div>
            <div className="setCount">{setEntry.count}</div>
          </div>
          <div className="buyPacks">
            <Select
              value={selectedOption}
              onChange={(option) => {
                if (option) {
                  dispatch({
                    type: "SET_SET_BUY_AMOUNT",
                    setId: setId,
                    setBuyAmount: Number(option.value),
                  });
                }
              }}
              options={options}
              isSearchable={false}
              styles={{
                control: (provided) => ({
                  ...provided,
                  minWidth: "100px",
                }),
              }}
            />
            {renderRequirementsMet()}
            <div
              className={`buyButton ${!canPurchase() ? "disabled" : ""}`}
              onClick={() => {
                if (canPurchase()) {
                  executeQuickBuy();
                }
              }}
            >
              Buy
            </div>
          </div>
        </div>
      </div>
      <SetDetails
        setId={setId}
        state={state}
        dispatch={dispatch}
        isOpen={showDetails}
        onClose={setShowDetails}
      />
      {showPopup &&
        createPortal(
          <div
            className="setPopup"
            style={{
              top: popupPosition.top,
              bottom: popupPosition.bottom,
              left: popupPosition.left,
            }}
          >
            {action.cost.length ? (
              <>
                <b className="costsHeader">Costs: </b>
                <div className="costContent">
                  {action.cost.map((cost, index) => renderCosts(index, cost))}
                </div>
              </>
            ) : null}
          </div>,
          document.body,
        )}
    </>
  );
};

export default SetRow;
